import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import ApiConfig from 'src/config/api-config';
import { GenerateProgramResponse } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGenerated/types';
import { ProgramGenerationPayload } from './useSummarizeInstructionProd';

interface PlanDataPayload {
  user_id: string;
  user_name: string;
  program_name: string;
  program_type: string;
  program_length: number;
  activation_date: string;
  equiptments: string[];
  focused_area: string;
  muscles: string[];
  selected_days: string[];
  workout_intensity: string;
  workout_type: string;
  workout_duration?: string;
  fitness_level?: string;
  energy_level?: string;
  daily_physical_load?: string;
  goals?: string;
  preferences?: string;
  injured_body_parts?: string;
  env?: 'dev' | 'prod';
}

function mapToPlanData(payload: any): PlanDataPayload {
  const isDev =
    typeof window !== 'undefined'
      ? window.location.hostname.includes('localhost') ||
        window.location.hostname.includes('127.0.0.1') ||
        window.location.hostname.includes('dev.3rddigital.com')
      : process.env.NODE_ENV === 'development';

  const envFlag: 'dev' | 'prod' = isDev ? 'dev' : 'prod';

  if (payload.user_id && payload.selected_days) {
    return {
      ...(payload as PlanDataPayload),
      env: payload.env || envFlag,
    };
  }

  const params = payload.parameters || {};
  const clientId = payload.clientId || payload.user_id || 'client_001';

  // Duration in weeks
  const durRaw = params.program_duration?.[0] || '4';
  const mWeeks = String(durRaw).match(/\d+/);
  const programLength = mWeeks ? parseInt(mWeeks[0], 10) : 4;

  // Days list
  const scheduleRaw = params.training_schedule ||
    params.how_often_train || ['monday', 'wednesday', 'friday'];
  const daysList: string[] = [];
  if (Array.isArray(scheduleRaw)) {
    scheduleRaw.forEach((s: any) => {
      String(s)
        .split(',')
        .forEach((part) => {
          const trimmed = part.trim();
          if (trimmed && trimmed.toLowerCase() !== 'none') {
            daysList.push(trimmed.charAt(0).toUpperCase() + trimmed.slice(1));
          }
        });
    });
  }
  if (daysList.length === 0) {
    daysList.push('Monday', 'Wednesday', 'Friday');
  }

  const goalRaw = params.training_goal?.[0] || 'increase-strength';
  const formatTitle = (s: string) =>
    s.replace(/[-_]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

  const goalTitle = formatTitle(goalRaw);

  const equipRaw = params.gym_equipments || params.selected_equipment || ['dumbell'];
  const equipList = (Array.isArray(equipRaw) ? equipRaw : [equipRaw]).filter(
    (e: any) => e && String(e).toLowerCase() !== 'none',
  );
  if (equipList.length === 0) {
    equipList.push('dumbell');
  }

  const intensityRaw = params.program_intensity?.[0] || params.intensity?.[0] || 'moderate';
  const intensityTitle = intensityRaw.charAt(0).toUpperCase() + intensityRaw.slice(1).toLowerCase();

  const focusedDetails = params.training_goal_details || [];
  const approaches = params.training_goal_approaches || [];
  const preferences = params.training_goal_preferences || [];

  const focusedArea =
    focusedDetails
      .filter((f: string) => f && f.toLowerCase() !== 'none')
      .map(formatTitle)
      .join(', ') || goalTitle;

  const muscles = [
    ...focusedDetails.filter((f: string) => f && f.toLowerCase() !== 'none').map(formatTitle),
    ...approaches.filter((a: string) => a && a.toLowerCase() !== 'none').map(formatTitle),
  ];
  if (muscles.length === 0) {
    muscles.push(goalTitle);
  }

  const todayYmd = new Date().toISOString().split('T')[0];

  return {
    user_id: clientId,
    user_name: payload.userName || `Client ${clientId.slice(0, 6)}`,
    program_name: payload.programName || `${goalTitle} Program`,
    program_type: goalRaw.replace(/-/g, '_'),
    program_length: programLength,
    activation_date: todayYmd,
    equiptments: equipList,
    focused_area: focusedArea,
    muscles: muscles,
    selected_days: daysList,
    workout_intensity: intensityTitle,
    workout_type: goalRaw.replace(/-/g, '_'),
    workout_duration: params.workout_duration?.[0] || '45-min',
    fitness_level: params.fitness_level?.[0] || 'intermediate',
    energy_level: params.energy_level?.[0] || 'moderate',
    goals: [goalTitle, ...preferences.map(formatTitle)].join('; '),
    preferences:
      [...preferences.map(formatTitle), ...approaches.map(formatTitle)].join('; ') ||
      'not specified',
    injured_body_parts:
      (params.injuries_limitations || [])
        .filter((i: string) => i && i.toLowerCase() !== 'none')
        .join(', ') || 'none',
    env: envFlag,
  };
}

export const useGenerateWorkoutProgramCoachProd = () => {
  return useMutation({
    mutationFn: async (
      payload: ProgramGenerationPayload,
    ): Promise<GenerateProgramResponse | null> => {
      try {
        const url = ApiConfig.GENERATE_WORKOUT_PROGRAM_COACH_PROD_API_HOST.startsWith('http')
          ? ApiConfig.GENERATE_WORKOUT_PROGRAM_COACH_PROD_API_HOST
          : `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.GENERATE_WORKOUT_PROGRAM_COACH_PROD_API_HOST}`;

        const planData = mapToPlanData(payload);

        const response = await axios.post<GenerateProgramResponse>(url, planData, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 600000, // 10 minutes
        });
        return response.data;
      } catch {
        // Return null instead of throwing an unhandled exception on 500/network error
        return null;
      }
    },
  });
};
